import type { OctTree } from '@data/OctTree';
import { Notification, PipelinePhase, SerializedPipeline } from '@visualizer/types';
import type { SensorData } from 'Simulations/SensorPoint';
import { BlockCatalog, BlockKeys } from './BlockCatalog';
import './blocks/CloseToDesignSchemesBlock';
import './blocks/DensityBlock';
import './blocks/HideZeroValuesBlock';
import './blocks/InvertBlock';
import './blocks/RemoveSensorsBlock';
import './blocks/ShadowBlock';
import './blocks/SolarParamsBlock';
import { ContextHelper, StageContext } from './Context';
import { BlockParameters, PipelineBlock } from './PipelineBlock';
import SortStage from './stages/preprocess/SortStage';

/**
 * Result of pipeline execution
 */
export interface PipelineExecutionResult {
    success: boolean;
    data: SensorData[];
    executionTime: string;
    blocksExecuted: string[]; // block ids that were executed
    errors?: Array<{
        blockId: string;
        blockName: string;
        error: Error;
    }>;
    notifications?: Notification[];
    executionContext: StageContext;
    outputMinMax: [number, number];
}

export type PipelineExecutionOptions = {
    suppressNotifications?: boolean;
    capturePhase?: PipelinePhase;
    octTree: OctTree;
};

/**
 * Default implementation of PipelineProcessor
 */
export class Pipeline {
    private static readonly PHASE_ORDER: PipelinePhase[] = [PipelinePhase.Pre, PipelinePhase.Regular, PipelinePhase.Post];
    private blocksByPhase: Map<PipelinePhase, PipelineBlock<any>[]> = Pipeline.createEmptyPhaseMap();
    private currentContext?: StageContext;

    constructor(serializedPipeline?: SerializedPipeline) {
        const initialPipeline = serializedPipeline || { blocks: [], executionCount: 0 };
        this.fromJSON(initialPipeline);
    }

    // Remove all blocks of the specified type:
    private removeBlocksByType(types: BlockKeys[]) {
        for (const block of this.getBlocksInPhaseOrder()) {
            if (types.includes(block.type as BlockKeys)) {
                this.removeBlock(block.id);
            }
        }
    }

    private generatePreBlocks() {
        // Check if all PRE blocks already exist:
        const preBlockTypes = [BlockKeys.Invert, BlockKeys.Density, BlockKeys.HideZeroValues, BlockKeys.SolarParameters];
        const hasAllPreBlocks = preBlockTypes.every((type) => this.getBlocks().some((block) => block.type === type));
        if (hasAllPreBlocks) return;

        // Remove existing ones and add them back:
        this.removeBlocksByType(preBlockTypes);

        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.Invert, {
                enabled: false,
            })
        );
        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.Density, {
                enabled: false,
                isOneEveryThree: false,
            })
        );
        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.HideZeroValues, {
                enabled: false,
            })
        );
        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.SolarParameters, {
                enabled: false,
                isCloudCoverOn: false,
                cloudCoverPercentage: 0,
                isDiffuseRadiationOn: false,
                diffuseRadiationPercentage: 0,
            })
        );
    }

    private generatePostBlocks() {
        // Check if all POST blocks already exist:
        const shadowBlocks = this.getBlocks().filter((block) => block.type === BlockKeys.Shadow);
        const removeSensorBlocks = this.getBlocks().filter((block) => block.type === BlockKeys.RemoveSensorsById);
        const closeToDsBlocks = this.getBlocks().filter((block) => block.type === BlockKeys.CloseToDesignSchemes);
        const hasValidShadowBlocks =
            shadowBlocks.length === 2 &&
            shadowBlocks.some((block) => !(block.getParams() as { isInverted?: boolean } | undefined)?.isInverted) &&
            shadowBlocks.some((block) => (block.getParams() as { isInverted?: boolean } | undefined)?.isInverted);
        const hasValidRemoveSensorBlocks =
            removeSensorBlocks.length === 2 &&
            removeSensorBlocks.some((block) => !(block.getParams() as { isInverted?: boolean } | undefined)?.isInverted) &&
            removeSensorBlocks.some((block) => (block.getParams() as { isInverted?: boolean } | undefined)?.isInverted);
        const hasValidCloseToDsBlock = closeToDsBlocks.length === 1;
        if (hasValidShadowBlocks && hasValidRemoveSensorBlocks && hasValidCloseToDsBlock) return;

        // Remove existing ones and add them back:
        this.removeBlocksByType([BlockKeys.Shadow, BlockKeys.RemoveSensorsById, BlockKeys.CloseToDesignSchemes]);

        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.Shadow, {
                enabled: false,
                polygons: [],
                isInverted: false,
                raycastMeshes: [],
                globalBox: undefined,
            })
        );

        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.Shadow, {
                enabled: false,
                isInverted: true,
                polygons: [],
                raycastMeshes: [],
                globalBox: undefined,
            })
        );

        for (let i = 0; i < 2; i++)
            this.addBlock(
                BlockCatalog.createBlock(BlockKeys.RemoveSensorsById, {
                    enabled: false,
                    sensorIds: new Set<number>(),
                    isInverted: i === 0,
                })
            );

        this.addBlock(
            BlockCatalog.createBlock(BlockKeys.CloseToDesignSchemes, {
                enabled: false,
                raycastMeshes: [],
                globalBox: undefined,
                maxDistance: 0,
            })
        );
    }

    private clearInternalState() {
        this.currentContext = undefined;
    }

    private static createEmptyPhaseMap(): Map<PipelinePhase, PipelineBlock<any>[]> {
        const tmpMap = new Map<PipelinePhase, PipelineBlock<any>[]>();
        for (const phase of Pipeline.PHASE_ORDER) {
            const createdBlocks: PipelineBlock<any>[] = [];
            tmpMap.set(phase, createdBlocks);
        }
        return tmpMap;
    }

    private getBlocksInPhaseOrder(): PipelineBlock<any>[] {
        const blocks: PipelineBlock<any>[] = [];
        for (const phase of Pipeline.PHASE_ORDER) {
            blocks.push(...this.blocksByPhase.get(phase)!);
        }
        return blocks;
    }

    private getPhaseStartIndex(phase: PipelinePhase): number {
        let startIndex = 0;
        for (const currentPhase of Pipeline.PHASE_ORDER) {
            if (currentPhase === phase) {
                return startIndex;
            }
            startIndex += this.blocksByPhase.get(currentPhase)!.length;
        }
        return startIndex;
    }

    private findBlockLocation(blockId: string): { phase: PipelinePhase; index: number; block: PipelineBlock<any> } | null {
        for (const phase of Pipeline.PHASE_ORDER) {
            const list = this.blocksByPhase.get(phase)!;
            const index = list.findIndex((b) => b.id === blockId);
            if (index !== -1) {
                return { phase, index, block: list[index] };
            }
        }
        return null;
    }

    private clearCachesFrom(phase: PipelinePhase, startIndexInPhase: number): void {
        let shouldInvalidate = false;
        for (const currentPhase of Pipeline.PHASE_ORDER) {
            const list = this.blocksByPhase.get(currentPhase)!;
            if (currentPhase === phase) {
                for (let i = startIndexInPhase; i < list.length; i++) {
                    list[i].clearCache();
                }
                shouldInvalidate = true;
                continue;
            }

            if (shouldInvalidate) {
                for (const block of list) {
                    block.clearCache();
                }
            }
        }
    }

    /**
     * Get all blocks in the pipeline
     */
    getBlocks(): PipelineBlock<any>[] {
        return this.getBlocksInPhaseOrder();
    }

    /**
     * Get a specific block by ID
     */
    getBlock(blockId: string): PipelineBlock<any> | undefined {
        return this.findBlockLocation(blockId)?.block;
    }

    /**
     * Add a block to the end of the pipeline
     */
    addBlock(block: PipelineBlock<any>): Pipeline {
        if (BlockCatalog.isUnique(block.type)) {
            const existingBlock = this.getBlocksInPhaseOrder().findIndex((b) => b.type === block.type);
            if (existingBlock !== -1) {
                throw new Error(`Block of type "${block.type}" is unique and already exists in the pipeline.`); // Think about if it is better throwing an error or just notify
            }
        }

        this.blocksByPhase.get(block.getPipelinePhase())!.push(block);
        return this;
    }

    /**
     * Insert a block at a specific position
     */
    /**
     * Remove a block from the pipeline
     */
    removeBlock(blockId: string): boolean {
        const location = this.findBlockLocation(blockId);
        if (!location) {
            return false;
        }

        const { phase, index } = location;
        const phaseList = this.blocksByPhase.get(phase)!;
        phaseList.splice(index, 1);

        if (phase === PipelinePhase.Pre) {
            this.clearCacheAll();
        } else {
            // Invalidate cache after this block within the phase and all later phases.
            this.clearCachesFrom(phase, index);
        }
        return true;
    }

    /**
     * Move a block to a new position
     */
    moveBlock(blockId: string, newPosition: number): boolean {
        const location = this.findBlockLocation(blockId);
        if (!location) {
            return false;
        }

        const { phase, index: oldIndex, block } = location;
        const phaseList = this.blocksByPhase.get(phase)!;
        const phaseStartIndex = this.getPhaseStartIndex(phase);
        const clampedPosition = Math.max(0, Math.min(newPosition - phaseStartIndex, phaseList.length - 1));

        if (clampedPosition === oldIndex) {
            return true;
        }

        // Remove from old position
        phaseList.splice(oldIndex, 1);

        // Insert at new position
        phaseList.splice(clampedPosition, 0, block);

        // Invalidate cache after this block within the phase and all later phases.
        const startIndex = Math.min(oldIndex, clampedPosition);
        if (phase === PipelinePhase.Pre) {
            this.clearCacheAll();
        } else {
            this.clearCachesFrom(phase, startIndex);
        }

        return true;
    }

    /**
     * Enable or disable a block
     */
    toggleBlock(blockId: string, status?: boolean): boolean {
        const location = this.findBlockLocation(blockId);
        if (!location) {
            return false;
        }
        const { phase, index: blockIndex, block } = location;

        if (status === undefined) {
            block.isEnabled() ? block.disable() : block.enable();
        } else if (status) {
            block.enable();
        } else {
            block.disable();
        }

        block.clearCache();

        if (block.getPipelinePhase() === PipelinePhase.Pre) {
            // Pre-phase blocks run before every other phase, regardless of list order.
            // Clear all caches to avoid stale downstream data when toggling them.
            this.clearCacheAll();
            return true;
        }

        // Invalidate cache after this block (list order).
        this.clearCachesFrom(phase, blockIndex + 1);

        return true;
    }

    /**
     * Update parameters for a specific block
     */
    updateBlockParams<T extends BlockParameters>(blockId: string, params: Partial<T>): boolean {
        const block = this.getBlock(blockId);

        if (!block) {
            return false;
        }

        block.updateParams(params);
        return true;
    }

    /**
     * Clear all blocks from the pipeline
     */
    clearPipeline(): void {
        this.blocksByPhase = Pipeline.createEmptyPhaseMap();
    }

    /**
     * Clear cache for all blocks
     */
    clearCacheAll(): void {
        for (const block of this.getBlocksInPhaseOrder()) {
            block.clearCache();
        }
    }

    /**
     * Get the current execution context (only valid during execution)
     */
    getCurrentContext(): StageContext {
        if (!this.currentContext) {
            throw new Error('Pipeline not executing');
        }
        return this.currentContext;
    }

    /**
     * Execute the pipeline with the given input data
     */
    execute(input: SensorData[], absoluteMinMax: [number, number], options: PipelineExecutionOptions): PipelineExecutionResult {
        const startTime = performance.now();
        const blocksExecuted: string[] = [];
        const errors: Array<{ blockId: string; blockName: string; error: Error }> = [];
        const notifications: Notification[] = [];
        let success = true;
        let stopExecution = false;

        const { suppressNotifications = false, capturePhase = PipelinePhase.Post, octTree } = options;
        this.currentContext = ContextHelper.create([...input], absoluteMinMax, absoluteMinMax, octTree); // Clone to avoid mutating input

        let invalidateDownstream = false;

        const runBlock = (block: PipelineBlock<any>) => {
            if (stopExecution) return;
            // Invalidate cache if previous block did not use it (even if disabled):
            if (invalidateDownstream) block.clearCache();
            if (!block.isEnabled()) return;

            try {
                const { context, usedCache } = block.execute(this.currentContext as StageContext);
                this.currentContext = context;
                if (!usedCache) {
                    invalidateDownstream = true;
                    blocksExecuted.push(block.name);
                }
                if (!suppressNotifications) notifications.push(...block.getNotifications());
            } catch (error: any) {
                success = false;
                console.error(error);
                errors.push({
                    blockId: block.id,
                    blockName: block.name,
                    error: error instanceof Error ? error : new Error(String(error)),
                });
                stopExecution = true;
            }
        };

        const runPhase = (phase: PipelinePhase) => {
            const phaseBlocks = this.blocksByPhase.get(phase)!;
            for (const block of phaseBlocks) {
                runBlock(block);
            }
        };

        runPhase(PipelinePhase.Pre);
        if (!stopExecution && capturePhase !== PipelinePhase.Pre) {
            runPhase(PipelinePhase.Regular);
        }
        if (!stopExecution && capturePhase === PipelinePhase.Post) {
            runPhase(PipelinePhase.Post);
        }

        const sortStage = new SortStage();
        this.currentContext = sortStage.execute(this.currentContext);

        const outputMinMax: [number, number] = [
            this.currentContext.sensors[0]?.getScalarValue() || 0,
            this.currentContext.sensors[this.currentContext.sensors.length - 1]?.getScalarValue() || 1,
        ];
        this.postProcessContext(this.currentContext, outputMinMax);

        const endTime = performance.now();
        const result = {
            success,
            data: this.currentContext.sensors,
            executionTime: `${endTime - startTime} ms`,
            blocksExecuted,
            errors: errors.length > 0 ? errors : undefined,
            notifications: notifications.length > 0 ? notifications : undefined,
            executionContext: this.currentContext,
            outputMinMax,
        };
        this.clearInternalState();
        return result;
    }

    private postProcessContext(context: StageContext, outputMinMax: [number, number]) {
        context.remappedMinMax = [
            Math.min(outputMinMax[0], context.remappedMinMax[0]),
            Math.max(outputMinMax[1], context.remappedMinMax[1]),
        ];

        context.absoluteMinMax = [
            Math.min(context.remappedMinMax[0], context.absoluteMinMax[0]),
            Math.max(context.remappedMinMax[1], context.absoluteMinMax[1]),
        ];
    }
    /**
     * Serialize the pipeline configuration.
     */
    public toJSON(): SerializedPipeline {
        return {
            blocks: this.getBlocksInPhaseOrder().map((b) => b.toJSON()),
            executionCount: 0, // Placeholder, actual execution count should be managed externally
        };
    }

    /**
     * Deserialize a pipeline from JSON. When you use it, the blocks will be recreated and replaced.
     */
    public fromJSON(serialized: SerializedPipeline): void {
        this.blocksByPhase = Pipeline.createEmptyPhaseMap();
        serialized.blocks.forEach((b) => {
            const block = BlockCatalog.createBlock(b.type, b.params, b.id);
            this.blocksByPhase.get(block.getPipelinePhase())!.push(block);
        });
        this.generatePreBlocks();
        this.generatePostBlocks();
    }
}
